import CanvasRenderer from "../../canvas/canvasRenderer";
import { drawShadedImage, applyPixelShadingRules, hasCustomPixelColorSettings, exportBlob, getAnnotationType } from "../../canvas/canvasUtils";
import { addMode, editMode, deleteMode, magnifyMode, ANNOTATION_COLORS, ANNOTATION_MODES, shapeFillMode } from "../../config/config";
import axios from "axios";
import { mapActions, mapGetters } from "vuex";

export default {
  computed: {
    ...mapGetters(["sideBySideIsSynced"]),
    exam() {
      return this.context && this.context.exam;
    },
    isOptos() {
      return this.context && this.context.exam.isOptos;
    },
    isOCT() {
      return this.context && this.context.exam.isOCT;
    },
    isFlipped() {
      return this.context && this.context.isFlipped;
    },
    isFlipped90() {
      return this.context && this.context.isFlipped90;
    },
    isDeepDeeAnnotation() {
      return this.context && this.context.showDeepDeeAnnotation;
    },
    deepDeeAnnotation() {
      return this.context && this.context.deepDeeAnnotation;
    },

    // #region memory canvases
    saveCanvas() {
      return this.context && this.context.saveCanvas;
    },
    flattenedImageReady() {
      return this.context && this.context.flattenedImageReady;
    },
    // #endregion

    // #region current image
    currentBrowserImage() {
      return this.context && this.context.image;
    },
    imgNotFound() {
      return !!(this.currentBrowserImage && this.currentBrowserImage.imgNotFound);
    },
    imgAvailable() {
      return !!(this.currentBrowserImage && !this.currentBrowserImage.imgNotFound);
    },
    hasFullImage() {
      return !!(this.imgSize && this.imgSize.w && this.imgSize.h);
    },
    imgObject() {
      return this.currentBrowserImage && this.currentBrowserImage.imgHtmlObject;
    },
    thumbObject() {
      return this.currentBrowserImage && this.currentBrowserImage.thumbHtmlObject;
    },
    displayImgObject() {
      return this.hasFullImage ? this.imgObject : this.thumbObject;
    },
    imgSize() {
      return this.currentBrowserImage && this.currentBrowserImage.imgSize;
    },
    thumbSize() {
      return this.currentBrowserImage && this.currentBrowserImage.thumbSize;
    },
    displayImgSize() {
      return this.hasFullImage ? this.imgSize : this.thumbSize;
    },
    imgIsLoading() {
      return this.currentBrowserImage && this.currentBrowserImage.imgIsLoading;
    },
    thumbIsLoading() {
      return this.currentBrowserImage && this.currentBrowserImage.thumbIsLoading;
    },
    imgEye() {
      return this.currentBrowserImage && this.currentBrowserImage.imgEye;
    },
    // #endregion

    // #region color settings
    imageColorSettings() {
      return this.context && this.context.imageColorSettings;
    },
    grayscaleMode() {
      return this.imageColorSettings && this.imageColorSettings.grayscaleMode;
    },
    brightness() {
      return this.imageColorSettings && this.imageColorSettings.brightness;
    },
    contrast() {
      return this.imageColorSettings && this.imageColorSettings.contrast;
    },
    gamma() {
      return this.imageColorSettings && this.imageColorSettings.gamma;
    },
    greenBrightness() {
      return this.imageColorSettings && this.imageColorSettings.greenBrightness;
    },
    greenContrast() {
      return this.imageColorSettings && this.imageColorSettings.greenContrast;
    },
    // #endregion

    // #region Annotations
    annotations() {
      return this.context && this.context.annotations;
    },
    annotationMode() {
      return this.context && this.context.annotationMode;
    },
    annotationColor() {
      return this.context && this.context.annotationColor;
    },
    annotationOptions() {
      return {
        textSize: this.context && this.context.annotationTextSize || 40,
        lineWidth: this.context && this.context.annotationLineWidth || 5,
        color: this.context && this.context.annotationColor || ANNOTATION_COLORS.White,
        scale: this.context && this.context.deviceCalibration && this.context.deviceCalibration.value,
        transform: this.context && this.context.transform
      };
    },
    annotationLineWidth() {
      return this.context && this.context.annotationLineWidth;
    },
    annotationTextSize() {
      return this.context && this.context.annotationTextSize;
    },
    isDragLock() {
      return this.context && this.context.isDragLock;
    },
    inAddMode() {
      return addMode(this.annotationMode);
    },
    inEditMode() {
      return editMode(this.annotationMode);
    },
    inDeleteMode() {
      return deleteMode(this.annotationMode);
    },
    inMagnifyMode() {
      return magnifyMode(this.annotationMode);
    },
    inShapeFillMode() {
      return shapeFillMode(this.annotationMode);
    },
    syncInShapeFillMode() {
      return shapeFillMode(this.syncContext.annotationMode);
    },
    isMagnifying() {
      return this.context && this.context.isMagnifying;
    },
    magnifier() {
      return this.context && this.context.magnifier;
    },
    activeAnnotation() {
      return this.context && this.context.activeAnnotation;
    },
    syncActiveAnnotation() {
      return this.syncContext && this.syncContext.activeAnnotation;
    },
    selectedTextSize() {
      return this.context && this.context.annotationTextSize;
    },
    
    shapeFillColor() {
      return this.context && this.context.shapeFillColor;
    },
    
    // #endregion

    // #region RGB view
    rgbSettings() {
      return this.context && this.context.rgbSettings;
    },
    rgbView() {
      return !!this.rgbSettings;
    },
    rgbSync() {
      return this.rgbSettings && this.rgbSettings.sync;
    },
    greenSync() {
      return this.rgbSettings && this.rgbSettings.greenSync;
    },
    // #endregion

    // #region Device configurations for measurements
    deviceConfigs() {
      return this.context && this.context.deviceConfigs;
    },
    hasDeviceConfigs() {
      return this.deviceConfigs.length;
    },
    deviceCalibrations() {
      return this.deviceConfigs.filter(v => v.type === "magVal");
    },
    hasDeviceCalibrations() {
      return this.deviceCalibrations.length;
    },
    deviceCalibration() {
      return this.context && this.context.deviceCalibration;
    },
    // #endregion
  },
  methods: {
    ...mapActions(["downloadBlob"]),
    // actions/mutations

    // #region select images
    resetImage() {
      if (!this.context.deepDeeHighlight) {
        this.context.selectImage(this.context.image);
        if (this.sideBySideIsSynced)
          this.syncContext.selectImage(this.syncContext.image);
      }
    },
    setCurrentImageLoading(isLoading) {
      if (this.context && this.context.image)
        this.context.image.imgIsLoading = isLoading;
    },
    // #endregion

    // #region annotations and measurements
    clearAnnotations() {
      if (!this.context.deepDeeHighlight) {
        if (this.context)
        this.context.clearAnnotations();
      if (this.sideBySideIsSynced && this.syncContext)
        this.syncContext.clearAnnotations();
      }
    },
    annotationModeButtonClick(buttonMode) {
      if (!this.context.deepDeeHighlight) 
        this.toggleAnnotationMode(buttonMode);
    },
    dragButtonClick() {
      if (this.context) {
        this.context.toggleIsDragLock();
      }
      if (this.sideBySideIsSynced && this.syncContext) {
        this.syncContext.toggleIsDragLock();
      }
      this.clearActiveAnnotation();
    },
    clearAnnotationMode() {
      if (this.context)
        this.context.clearAnnotationMode();
      if (this.sideBySideIsSynced && this.syncContext)
        this.syncContext.clearAnnotationMode();
    },
    toggleAnnotationMode(mode) {
      if (this.context)
        this.context.toggleAnnotationMode(mode);
      if (this.sideBySideIsSynced && this.syncContext)
        this.syncContext.toggleAnnotationMode(mode);
    },
    setAnnotationMode(mode) {
      if (this.context)
        this.context.setAnnotationMode(mode);
      if (this.sideBySideIsSynced && this.syncContext)
        this.syncContext.setAnnotationMode(mode);
    },
    updateAnnotationColor(color) {
      if (this.context)
        this.context.updateAnnotationColor(color);
      if (this.sideBySideIsSynced && this.syncContext)
        this.syncContext.updateAnnotationColor(color);
    },
    updateShapeFillColor(color) {
      if (this.context)
        this.context.updateShapeFillColor(color);
      if (this.sideBySideIsSynced && this.syncContext)
        this.syncContext.updateShapeFillColor(color);
    },
    selectCalibration(name) {
      if (this.context)
        this.context.selectCalibration(name);
      if (this.sideBySideIsSynced && this.syncContext)
        this.syncContext.selectCalibration(name);
    },
    clearActiveAnnotation() {
      if (this.context)
        this.context.clearActiveAnnotation();
      if (this.sideBySideIsSynced && this.syncContext)
        this.syncContext.clearActiveAnnotation();
    },
    updateTextSize(size) {
      this.setFlattenedImageReady(false);

      if (this.context)
        this.context.updateAnnotationTextSize(size);
      
      if (this.sideBySideIsSynced && this.syncContext)
        this.syncContext.updateAnnotationTextSize(size);
    },
    updateLineWidth(size) {
      this.setFlattenedImageReady(false);
      
      if (this.context)
        this.context.updateLineWidth(size);
        
      if (this.sideBySideIsSynced && this.syncContext)
        this.syncContext.updateLineWidth(size);
    },
    mousedownAnnotation(eventWrapper) {
      this.setFlattenedImageReady(false);
      this.doMousedownAnnotation(eventWrapper, false);
      if (this.sideBySideIsSynced)
        this.doMousedownAnnotation(eventWrapper, true);
    },
    doMousedownAnnotation(eventWrapper, sync) {
      let activeAnnotation = sync ? this.syncActiveAnnotation : this.activeAnnotation;
      if (activeAnnotation && activeAnnotation.isContinuous) {
        activeAnnotation.mousedown(eventWrapper.x, eventWrapper.y, eventWrapper.e);
      } else {
        let previousActive = activeAnnotation;
        this.doClearActiveAnnotation(sync);
        let annotation = null;
        if (this.inAddMode) 
          annotation = this.doCreateAnnotation(eventWrapper, sync);
        else if (this.inEditMode || (sync ? this.syncInShapeFillMode : this.inShapeFillMode)) 
          annotation = this.doGetAnnotationByPosition(eventWrapper.x, eventWrapper.y, sync);
        if (this.isDragLock && this.inEditMode) 
        {
          if (annotation == previousActive) 
            this.doSetActiveAnnotation(annotation, sync);
        }
        else
          this.doSetActiveAnnotation(annotation, sync);
        if (annotation) {
          if (this.inDeleteMode) this.doDeleteActiveAnnotation(sync);
          else if (this.inShapeFillMode && !sync) {
            this.annotations[this.annotations.indexOf(annotation)].color = this.shapeFillColor;
            annotation.updateColor(this.shapeFillColor);
            if (!this.syncInShapeFillMode) {
              this.clearAnnotationMode();
              this.generateFlatImage();
            }
          }
          else if (this.syncInShapeFillMode) {
            this.syncContext.annotations[this.syncContext.annotations.indexOf(annotation)].color = this.syncContext.shapeFillColor;
            annotation.updateColor(this.syncContext.shapeFillColor);
            this.clearAnnotationMode();
            this.generateFlatImage();
          }
          else if (this.isDragLock && this.inEditMode) 
          {
            if (annotation == activeAnnotation)
              annotation.mousedown(eventWrapper.x, eventWrapper.y, eventWrapper.e);
          }
          else {
            if (this.inAddMode && annotation.onCreate) annotation.onCreate();
            annotation.mousedown(eventWrapper.x, eventWrapper.y, eventWrapper.e);
          }
        }
      }
    },
    mousemoveAnnotation({ x, y }) {
      if (this.activeAnnotation)
        this.activeAnnotation.mousemove(x, y);
      if (this.sideBySideIsSynced && this.syncContext.activeAnnotation)
        this.syncContext.activeAnnotation.mousemove(x, y);
    },
    mouseupAnnotation() {
      if (this.activeAnnotation) this.activeAnnotation.mouseup();
      if (this.sideBySideIsSynced && this.syncContext.activeAnnotation)
        this.syncContext.activeAnnotation.mouseup();
      if (this.context.annotationMode == ANNOTATION_MODES.ContinuousDraw) this.clearActiveAnnotation();
      if (this.sideBySideIsSynced && this.syncContext.annotationMode == ANNOTATION_MODES.ContinuousDraw)
        this.syncContext.clearActiveAnnotation();
      if (this.inAddMode && this.context.annotationMode != ANNOTATION_MODES.ContinuousDraw) this.clearAnnotationMode();
    },

    annotationByPosition({x, y}) {
      return this.context && this.context.annotations && this.context.annotations.find(a => a.isInBounds(x, y));
    },
    doGetAnnotationByPosition(x, y, sync) {
      let context = sync ? this.syncContext : this.context;
      return context && context.annotations && context.annotations.find(a => a.isInBounds(x, y));
    },
    doCreateAnnotation(position, sync) {
      let context = sync ? this.syncContext : this.context;
      let type = getAnnotationType(this.annotationMode);
      let annotation = type && new type(position, this.annotationOptions, context);
      if (annotation) {
        annotation.setParentTransform(context.transform);
        if (sync && annotation.setSyncPair) {
          this.context.activeAnnotation.setSyncPair(annotation);
          annotation.setSyncPair(this.context.activeAnnotation);
        }
        this.addAnnotation(annotation, sync);
      }
      return annotation;
    },
    deleteActiveAnnotation() {
      this.doDeleteActiveAnnotation(false);
      if (this.sideBySideIsSynced && this.syncContext)
        this.doDeleteActiveAnnotation(true);
    },
    doDeleteActiveAnnotation(sync) {
      let context = sync ? this.syncContext : this.context;
      context.setFlattenedImageReady(false);
      let annotation = context.activeAnnotation;
      if (!annotation.delete()) return;
      let index = this.doGetAnnotationIndex(context.activeAnnotation, sync);
      context.clearActiveAnnotation();
      context.deleteAnnotation(index);
    },
    doSetActiveAnnotation(annotation, sync) {
      let context = sync ? this.syncContext : this.context;
      if (context) context.setActiveAnnotation(annotation);
    },
    addAnnotation(annotation, sync) {
      let context = sync ? this.syncContext : this.context;
      if (context) context.addAnnotation(annotation);
    },
    doGetAnnotationIndex(annotation, sync) {
      let context = sync ? this.syncContext : this.context;
      return context && context.annotations && context.annotations.findIndex(a => a == annotation);
    },
    doClearActiveAnnotation(sync) {
      let context = sync ? this.syncContext : this.context;
      context.clearActiveAnnotation();
    },
    setIsMagnifying(val, sync) {
      let context = sync ? this.syncContext : this.context;
      if (context) context.setIsMagnifying(val);
    },
    updateMagnifier(position) {
      this.doUpdateMagnifier(position, false);
      if (this.sideBySideIsSynced)
        this.doUpdateMagnifier(position, true);
    },
    doUpdateMagnifier(position, sync) {
      let context = sync ? this.syncContext : this.context;
      context.updateMagnifier(position);
    },
    mousedownMagnifier() {
      this.doMousedownMagnifier(false);
      if (this.sideBySideIsSynced)
        this.doMousedownMagnifier(true);
    },
    doMousedownMagnifier(sync) {
      let context = sync ? this.syncContext : this.context;
      context.mousedownMagnifier();
    },
    mouseupMagnifier() {
      this.doMouseupMagnifier(false);
      if (this.sideBySideIsSynced)
        this.doMouseupMagnifier(true);
    },
    doMouseupMagnifier(sync) {
      let context = sync ? this.syncContext : this.context;
      context.mouseupMagnifier();
    },
    // #endregion

    // #region DeepDee 

    toggleDeepDeeHighlight() {
      if (this.context.toggleDeepDeeHighlight && !this.context.showDeepDeeAnnotation)
        this.context.toggleDeepDeeHighlight();
    },
    // #endregion

    // #region color correction
    setFlattenedImageReady(ready) {
      if (this.context)
        this.context.setFlattenedImageReady(ready);
      if (this.sideBySideIsSynced)
        this.syncContext.setFlattenedImageReady(ready);
    },
    toggleGrayscaleMode(mode) {
      if (!this.context.deepDeeHighlight) {
        this.setFlattenedImageReady(false);
        if (this.rgbView)
          this.selectRGBViewByMode(mode);
        else if (this.context && this.context.toggleGrayscaleMode)
          this.context.toggleGrayscaleMode(mode);
      }
    },
    updateBrightness(brightness) {
      if (this.flattenedImageReady)
        this.setFlattenedImageReady(false);
      if (this.context && this.context.updateBrightness)
        this.context.updateBrightness(brightness);
      if (this.context && this.rgbView)
        this.updateRGBSync();
    },
    updateContrast(contrast) {
      if (this.context && this.flattenedImageReady)
        this.setFlattenedImageReady(false);
      if (this.context && this.context.updateContrast)
        this.context.updateContrast(contrast);
      if (this.context && this.rgbView)
        this.updateRGBSync();
    },
    updateGamma(gamma) {
      if (this.flattenedImageReady)
        this.setFlattenedImageReady(false);
      if (this.context.updateGamma)
        this.context.updateGamma(gamma);
      if (this.rgbView)
        this.updateRGBSync();
    },
    toggleGreenSync() {
      if (this.context.toggleGreenSync)
        this.context.toggleGreenSync();
      if (this.greenSync)
        this.updateRGBSync();
    },
    updateGreenBrightness(brightness) {
      if (this.flattenedImageReady)
        this.setFlattenedImageReady(false);
      if (this.context.updateGreenBrightness)
        this.context.updateGreenBrightness(brightness);
      if (this.rgbView)
        this.updateRGBSync();
    },
    updateGreenContrast(contrast) {
      if (this.flattenedImageReady)
        this.setFlattenedImageReady(false);
      if (this.context.updateGreenContrast)
        this.context.updateGreenContrast(contrast);
      if (this.rgbView)
        this.updateRGBSync();
    },
    // #endregion

    // #region transformations
    toggleFlipped() {
      this.clearAnnotationMode();
      this.setFlattenedImageReady(false);
      if (this.context.toggleFlipped)
        this.context.toggleFlipped();

      if (this.sideBySideIsSynced && this.syncContext && this.syncContext.toggleFlipped)
        this.syncContext.toggleFlipped();
    },
    toggleFlipped90() {
      this.clearAnnotationMode();
      this.setFlattenedImageReady(false);
      if (this.context.toggleFlipped90)
        this.context.toggleFlipped90();

      if (this.sideBySideIsSynced && this.syncContext && this.syncContext.toggleFlipped90)
        this.syncContext.toggleFlipped90();
    },

    toggleMouseWheelZoom() {
      if (this.context)
        this.context.toggleMouseWheelZoom();
      if (this.sideBySideIsSynced && this.syncContext)
        this.syncContext.toggleMouseWheelZoom();
    },
    // #endregion

    // #region rgb
    toggleRGB() {
      if (!this.context.deepDeeHighlight) {
        if (this.context && this.context.toggleRGB)
          this.context.toggleRGB();
        if (this.rgbView)
          this.replicateMainToAllRGB();
      }
    },
    toggleRGBSync() {
      if (this.context && this.context.toggleRGBSync)
        this.context.toggleRGBSync();
      if (this.rgbView)
        this.updateRGBSync();
    },
    selectRGBViewByMode(mode) {
      let index = this.getRGBIndex(mode);
      if (index === this.currentRGBIndex) index = 0;
      this.selectRGBView(index);
    },
    getRGBIndex(mode) {
      return this.context && this.context.getRGBIndex && this.context.getRGBIndex(mode);
    },
    selectRGBView(index) {
      if (this.rgbView && this.currentRGBIndex !== index) {
        this.setFlattenedImageReady(false);
        if (this.context && this.context.selectRGBView) this.context.selectRGBView(index);
        this.replicateCurrentRGBToMain();
      }
    },
    replicateCurrentRGBToMain() {
      if (this.context && this.context.replicateCurrentRGBToMain)
        this.context.replicateCurrentRGBToMain();
    },
    replicateMainToCurrentRGB() {
      if (this.context && this.context.replicateMainToCurrentRGB)
        this.context.replicateMainToCurrentRGB();
    },
    replicateMainToAllRGB() {
      if (this.context && this.context.replicateMainToAllRGB)
        this.context.replicateMainToAllRGB();
    },
    replicateGreenMainToAllRGB() {
      if (this.context && this.context.replicateGreenMainToAllRGB)
        this.context.replicateGreenMainToAllRGB();
    },
    updateRGBSync() {
      this.replicateMainToCurrentRGB();
      if (this.rgbSync)
        this.replicateMainToAllRGB();
      if (this.greenSync)
        this.replicateGreenMainToAllRGB();
    },
    // #endregion

    // #region image flattening
    generateFlatImage() {
      return new Promise(resolve => {
        if (this.saveCanvas && this.imgObject) {
          this.clearActiveAnnotation();
          let canvas = this.saveCanvas;
          let img = this.imgObject;
          let settings = this.imageColorSettings;

          if (!this.isFlipped90) {
            canvas.width = img.width;
            canvas.height = img.height;
          }
          else {
            canvas.width = img.height;
            canvas.height = img.width;
          }
          let renderer = new CanvasRenderer(canvas);
          renderer.doScaleToPixelRatio = false;
          renderer.canvasSize = this.imgSize;
          renderer.imgSize = this.imgSize;
          renderer.isFlipped = this.isFlipped;
          renderer.isFlipped90 = this.isFlipped90;
          renderer.isDeepDeeAnnotation = this.isDeepDeeAnnotation;
          renderer.toSave = true;
          if (hasCustomPixelColorSettings(settings)) {
            // note: this method puts image data directly onto the canvas
            drawShadedImage(img, canvas, applyPixelShadingRules, settings);
          } else {
            renderer.drawImg(img);
          }
          // note: using "render" instead of "draw" here because draw erases the canvas!
          let ctx = canvas.getContext("2d");
          this.annotations.forEach(a => renderer.renderAnnotation(a, ctx));
          if (renderer.isDeepDeeAnnotation)
            renderer.renderAnnotation(this.deepDeeAnnotation, ctx);

          this.setFlattenedImageReady(true);
          resolve();
        }
      });
    },

    lineWidthGenerateFlatImage() {
      return new Promise(resolve => {
        let ann = null;
        let sideBySideAnn = null;
        if (this.activeAnnotation)
          ann = this.activeAnnotation;
        if (this.sideBySideIsSynced)
          sideBySideAnn = this.syncContext.activeAnnotation;
        this.clearActiveAnnotation();
        let canvas = this.saveCanvas;
        let img = this.currentBrowserImage;
        let settings = this.imageColorSettings;

        canvas.width = img.width;
        canvas.height = img.height;
        let renderer = new CanvasRenderer(canvas);
        renderer.imgSize = this.imgSize;
        renderer.isFlipped = this.isFlipped;
        renderer.isFlipped90 = this.isFlipped90;
        if (hasCustomPixelColorSettings(settings)) {
          // note: this method puts image data directly onto the canvas
          drawShadedImage(img, canvas, applyPixelShadingRules, settings);
        } else {
          renderer.drawImg(img);
        }

        if (ann)
          this.doSetActiveAnnotation(ann, false);

        if (this.sideBySideIsSynced)
          this.doSetActiveAnnotation(sideBySideAnn, true);

        // note: using "render" instead of "draw" here because draw erases the canvas!
        let ctx = canvas.getContext("2d");
        this.annotations.forEach(a => renderer.renderAnnotation(a, ctx));

        this.setFlattenedImageReady(true);

        resolve();
      });
    },
    // #endregion

    // #region saveImages
    saveImage() {
      if (!this.context.deepDeeHighlight) {
        this.setCurrentImageLoading(true);
        this.exportFlatImage()
          .then(this.uploadAnnotatedImage)
          .then(msg => this.$store.commit("displayStatus", { message: msg, level: "success" }))
          .catch(msg => this.$store.commit("displayStatus", { message: msg, level: "error" }))
          .then(() => this.$store.dispatch("getPatientExams"))
          .finally(() => this.setCurrentImageLoading(false));
      }
    },
    uploadAnnotatedImage(blob) {
      const fd = new FormData();
      fd.append("image", blob);
      fd.append("ptSrNo", this.currentBrowserImage.ptSrNo);
      fd.append("exSrNo", this.currentBrowserImage.exSrNo);
      fd.append("eye", this.imgEye);
      fd.append("index", this.currentBrowserImage.imgIndex);
      fd.append("flSrNo", this.currentBrowserImage.flSrNo);
      fd.append("flName", this.currentBrowserImage.flName);
      fd.append("imageType", this.currentBrowserImage.imgType);
      fd.append("imageGroup", this.currentBrowserImage.imgGroup);
      fd.append("rcsSrNo", this.currentBrowserImage.rcsSrNo || 0);
      fd.append("rcimgIntrimPath", this.currentBrowserImage.rcimgIntrimPath || "");

      return axios.post("api/image/saveAnnotatedImage", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      }).then(response => {
        if (response.data.success) return "Annotated Image Saved.";
        return Promise.reject("Error Saving Annotated Image");
      }).catch(error => {
        if ((typeof error) === "string") return Promise.reject(error);
        return Promise.reject(error.message);
      });
    },
    downloadImage() {
      if (!this.context.deepDeeHighlight) {
        this.setCurrentImageLoading(true);
        this.exportFlatImage()
          .then(blob => {
            let filename = this.currentBrowserImage.imgType + "_" + this.currentBrowserImage.imgIndex + "_(0)." + (this.$store.getters.downloadPNGImage ? "png" : "jpg");
            this.downloadBlob({ blob, fileName: filename });
          })
          .finally(() => {
            this.setCurrentImageLoading(false);
          });
      }
    },
    exportFlatImage() {
      if (!this.imgObject) return Promise.reject();
      return this.generateFlatImage()
        .then(() => exportBlob(this.saveCanvas, this.$store.getters.downloadPNGImage ? "image/png" : "image/jpeg"));
    }
    // #endregion
  }
};